<!-- Complete form template with title, error display, and fixed autocomplete -->
<h2>Create Critical Date</h2>

<!-- Error Display Section -->
@if (hasValidationErrors && formErrors.length > 0) {
  <div class="error-display">
    <h4>Please fix the following issues:</h4>
    <ul>
      @for (error of formErrors; track error) {
        <li>{{ error }}</li>
      }
    </ul>
  </div>
}

<form #criticalDateForm="ngForm" (ngSubmit)="onSubmit()">
  
  <div class="form-grid">
    
    <!-- Date Type with Autocomplete -->
    <div class="form-field half-width">
      <mat-form-field class="full-width">
        <mat-label>Date Type</mat-label>
        <input 
          matInput
          [formControl]="typeControl"
          [matAutocomplete]="auto"
          placeholder="Start typing to search date types..."
          (input)="onTypeInput($event); validateFormOnChange()"
          required>
        <mat-autocomplete 
          #auto="matAutocomplete"
          [displayWith]="displayFn"
          (optionSelected)="onTypeSelected()">
          @for (option of (filteredDateTypes | async) || []; track option) {
            <mat-option [value]="option">{{ option }}</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
    </div>

    <!-- Assigned To (Multi-select) -->
    <div class="form-field half-width">
      <mat-form-field class="full-width">
        <mat-label>Assigned To (optional)</mat-label>
        <mat-select
          [(ngModel)]="currentCriticalDate.assignedTo"
          name="assignedTo"
          multiple
          (ngModelChange)="validateFormOnChange()">
          @for (user of allUsers; track user.email) {
            <mat-option [value]="user.email">
              {{ user.friendlyName }}
            </mat-option>
          }
        </mat-select>
        <mat-hint>Select one or more users to assign this date to</mat-hint>
      </mat-form-field>
    </div>

    <!-- Assigned By (Required single-select) -->
    <div class="form-field half-width">
      <mat-form-field class="full-width">
        <mat-label>Assigned By</mat-label>
        <mat-select
          [(ngModel)]="currentCriticalDate.assignedBy"
          name="assignedBy"
          required
          (ngModelChange)="validateFormOnChange()">
          @for (user of allUsers; track user.email) {
            <mat-option [value]="user.email">
              {{ user.friendlyName }}
            </mat-option>
          }
        </mat-select>
        <mat-hint>Who is creating/assigning this date</mat-hint>
      </mat-form-field>
    </div>

    <!-- Auto-generated Name (readonly) -->
    <div class="form-field full-width">
      <mat-form-field class="full-width">
        <mat-label>Name</mat-label>
        <input 
          matInput 
          [(ngModel)]="currentCriticalDate.name"
          name="name"
          readonly
          placeholder="Will be auto-generated from type">
      </mat-form-field>
    </div>

    <!-- Description -->
    <div class="form-field full-width">
      <mat-form-field class="full-width">
        <mat-label>Required Action or Comments</mat-label>
        <textarea 
          matInput 
          [(ngModel)]="currentCriticalDate.description"
          name="description"
          rows="3"
          placeholder="Describe what needs to be done..."
          (ngModelChange)="validateFormOnChange()">
        </textarea>
      </mat-form-field>
    </div>

    <!-- Event Date -->
    <div class="form-field half-width">
      <mat-form-field class="full-width">
        <mat-label>Important Date</mat-label>
        <input 
          matInput 
          [matDatepicker]="eventDatePicker"
          [value]="getEventDateAsDate()"
          (dateChange)="onEventDateChange($event); validateFormOnChange()"
          name="eventDate"
          required>
        <mat-datepicker-toggle matSuffix [for]="eventDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #eventDatePicker></mat-datepicker>
      </mat-form-field>
    </div>

    <!-- Reminder Date -->
    <div class="form-field half-width">
      <mat-form-field class="full-width">
        <mat-label>Reminder Date (optional)</mat-label>
        <input 
          matInput 
          [matDatepicker]="reminderDatePicker"
          [value]="getReminderDateAsDate()"
          (dateChange)="onReminderDateChange($event); validateFormOnChange()"
          name="reminderDate">
        <mat-datepicker-toggle matSuffix [for]="reminderDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #reminderDatePicker></mat-datepicker>
      </mat-form-field>
      <!-- Say what the blank will become. The save defaults it to the event date so the
           reminder job can actually select the record; leaving that silent is how people
           end up believing a reminder is scheduled when none is. -->
      @if ((currentCriticalDate.emailNotificationEnabled || currentCriticalDate.calendarNotificationEnabled)
           && !currentCriticalDate.reminderDate) {
        <p style="color: #666; font-size: 12px; margin: 4px 0 0 0;">
          No reminder date set — the reminder will be sent on the important date itself.
        </p>
      }
    </div>

    <!-- Repeat (recurring important date).
         Hidden wherever the roll-forward sweep in hc-all-orgs-scheduled-ops is not
         deployed: a toggle that promises a repeat and silently never fires is worse
         than no toggle. Gates the UI only — an existing `recurrence` is preserved. -->
    @if (showRecurrence) {
    <div class="form-field full-width">
      <app-date-recurrence-editor
        [recurrence]="currentCriticalDate.recurrence"
        (recurrenceChange)="onRecurrenceChange($event)"
        [anchorDate]="currentCriticalDate.eventDate"
        [disabled]="!hasNotification">
      </app-date-recurrence-editor>
      <!-- Repeat only reminds through email or the calendar entry — there are no
           in-app reminders — so it is locked until one of them is on. -->
      @if (!hasNotification) {
        <p style="color: #e65100; font-size: 12px; margin: 6px 0 0 4px;">
          @if (recurrenceAutoDisabled) {
            Repeat was turned off because no notifications are on — nobody would be told when
            the next occurrence comes due.
          }
          Turn on email or calendar notifications below to repeat this date.
        </p>
      }
    </div>
    }

    <!-- Email Notification Checkbox -->
    <div class="form-field half-width">
      <mat-checkbox
        [(ngModel)]="currentCriticalDate.emailNotificationEnabled"
        name="emailNotificationEnabled"
        color="primary"
        (ngModelChange)="onNotificationChange()">
        Send Email Notifications
      </mat-checkbox>
      @if (currentCriticalDate.emailNotificationEnabled && (!currentCriticalDate.assignedTo || currentCriticalDate.assignedTo.length === 0)) {
        <p style="color: #e65100; font-size: 12px; margin: 4px 0 0 32px;">
          No users assigned. Add users in "Assigned To" above so they receive email notifications.
        </p>
      }
    </div>

    <!-- Calendar Notification Checkbox -->
    <div class="form-field half-width">
      <mat-checkbox
        [(ngModel)]="currentCriticalDate.calendarNotificationEnabled"
        name="calendarNotificationEnabled"
        color="primary"
        (ngModelChange)="onNotificationChange()">
        Send Calendar Notification
      </mat-checkbox>
    </div>

  </div>

  <!-- SANDBOX-ONLY reminder test tool. environment.reminderTestTools is set in
       environment.sandbox-ng.ts only; the backend also refuses any other org. -->
  @if (showReminderTest && currentCriticalDate.criticalDateId) {
    <app-reminder-test-panel [criticalDateId]="currentCriticalDate.criticalDateId"></app-reminder-test-panel>
  }

  <!-- Action Buttons -->
  <div class="action-buttons">
    @if (!waiting) {
      <button 
        mat-raised-button 
        color="primary" 
        type="submit"
        [disabled]="!isFormValid()">
        Save Critical Date
      </button>
    }

    @if (waiting) {
      <mat-spinner diameter="30"></mat-spinner>
    }

    @if (!waiting) {
      <button 
        mat-raised-button 
        color="accent" 
        type="button"
        (click)="cancelSubmit()">
        Cancel
      </button>
    }
  </div>

  <!-- Completion Section with subtle styling -->
  <div class="completion-section">
    <h3>Completion Tracking</h3>
    
    <div class="completion-grid">
      <div class="completion-checkbox">
        <mat-checkbox 
          [(ngModel)]="currentCriticalDate.actionCompleted"
          name="actionCompleted"
          color="primary"
          (ngModelChange)="onActionCompletedChange($event)">
          Action Completed
        </mat-checkbox>
      </div>
      
      <div class="completion-notes">
        <mat-form-field class="full-width">
          <mat-label>Action Summary / Notes</mat-label>
          <input 
            matInput 
            [(ngModel)]="currentCriticalDate.actionSummary"
            name="actionSummary"
            placeholder="Describe what was completed...">
        </mat-form-field>
      </div>
    </div>
  </div>

</form>